import { useEffect, useState } from "react";

/**
 * usePwaInstall — exposes the `beforeinstallprompt` event so we can show
 * our own friendly "Install Meritz" button.
 *
 * Returns:
 *   - canInstall: true if the browser has fired beforeinstallprompt
 *   - isStandalone: true if the user is already inside the installed PWA
 *   - isIOS: true on iOS Safari (no beforeinstallprompt — needs manual share→add)
 *   - promptInstall(): triggers the native install prompt
 */
export default function usePwaInstall() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true);

  const isIOS =
    typeof window !== "undefined" &&
    /iPad|iPhone|iPod/.test(window.navigator.userAgent || "") &&
    !window.MSStream;

  const promptInstall = async () => {
    if (!deferred) return false;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      /* ignore */
    }
    setDeferred(null);
    return true;
  };

  return {
    canInstall: !!deferred && !isStandalone && !installed,
    isStandalone,
    isIOS,
    promptInstall,
  };
}
